import type { ViewerLabel } from '@tasks/bridge-client/host';
import { type VittoIdentity, vittoIdentitySchema } from '@tasks/contracts/auth';
import { useSyncExternalStore } from 'react';

/**
 * TASKS-APP-FULL-UI-1 U2c — WHO the Vitto session is, as the door learned it (the identity of `POST /auth/vitto`) and as the host labelled
 * the viewer (wire v1.2). Null when this document has no Vitto session (own login). It is what the UI READS to say the person's name and to
 * know whether it may offer writes; AUTHORITY stays with the server (`PolicyGuard` refuses every write of a viewer with 403 `read_only`).
 */
export type VittoSession = { identity: VittoIdentity; label: ViewerLabel | undefined };

let current: VittoSession | null = null;
/** Where the session is remembered across a reload — only the STANDALONE document has one (see `persistIn`); a framed document asks the host again. */
let storage: Storage | null = null;
export const STORAGE_KEY = 'tasks.vitto-session';
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

const write = (next: VittoSession | null): void => {
  try {
    if (next === null) storage?.removeItem(STORAGE_KEY);
    else storage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // no storage (private window, quota): the identity then lasts the document, as the refresh token would not survive either
  }
};

/** What was remembered, checked with the SAME schema the door uses (a hand-edited value is not a session). Unreadable ⇒ null. */
const read = (): VittoSession | null => {
  try {
    const raw: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? 'null');
    if (typeof raw !== 'object' || raw === null || !('identity' in raw)) return null;
    const identity = vittoIdentitySchema.safeParse(raw.identity);
    if (!identity.success) return null;
    const label = 'label' in raw && typeof raw.label === 'object' ? raw.label : undefined;
    return { identity: identity.data, label: (label ?? undefined) as ViewerLabel | undefined };
  } catch {
    return null;
  }
};

export const vittoSession = {
  get: (): VittoSession | null => current,
  set(next: VittoSession): void {
    current = next;
    write(next);
    notify();
  },
  clear(): void {
    current = null;
    write(null);
    notify();
  },
  /**
   * TASKS-APP-FULL-UI-1 U2b-2 — the STANDALONE document remembers who the Vitto session is (the refresh token already survives a reload; without
   * this a viewer who reloads would be offered every editor again). Cleared with a deliberate sign-out; `restore` brings it back at boot.
   */
  persistIn(target: Storage | null): void {
    storage = target;
  },
  restore(): void {
    const remembered = read();
    if (remembered === null) return;
    current = remembered;
    notify();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export const useVittoSession = (): VittoSession | null =>
  useSyncExternalStore(vittoSession.subscribe, vittoSession.get);

/** WHOSE the session is, as one string (`<workspaceId>:<userId>`): the owner of every local key and the guard of a renewal. */
export const ownerOf = (identity: VittoIdentity): string =>
  `${identity.workspaceId}:${identity.userId}`;

/** The name an embedded app shows for the viewer: what the host said, else the word the pass leaves us with. */
export const FALLBACK_NAME = 'Vitto user';
export const viewerNameOf = (session: VittoSession | null): string =>
  session?.label?.displayName ?? FALLBACK_NAME;

/** What the compact header of an app framed by Vitto is given (undefined: the product's own header, with its own account menu). */
export const embeddedViewer = (
  embedded: boolean,
  session: VittoSession | null,
): { name: string; canWrite: boolean } | undefined =>
  embedded ? { name: viewerNameOf(session), canWrite: canWriteOf(session) } : undefined;

/**
 * May this session OFFER writes? A Vitto session says so with `canWrite`; no Vitto session is the product as it was (the server's own
 * roles decide). Never an authority: a UI that offers a write the server refuses gets the 403 `read_only`, said in words.
 */
export const canWriteOf = (session: VittoSession | null): boolean =>
  session === null ? true : session.identity.canWrite && session.identity.role !== 'viewer';

/** What a SCREEN asks before it draws an editor, a «new» button or a drag handle: a viewer is shown the work, never the means to change it. */
export const useCanWrite = (): boolean => canWriteOf(useVittoSession());
